import type {
  Company, StatItem, ApproachStep, FutureTheme, WhyItem, Industry,
  Certification, NavItem, Social, LegalPage,
} from "../types";

export const COMPANY: Company = {
  name: "Aviokan Technologies Private Limited",
  shortName: "Aviokan",
  slogan: "Integrate. Innovate. Transform.",
  positioning: "Enterprise Technology • System Integration • Cybersecurity • Cloud • Digital Engineering",
  email: "connect@aviokan.com",
  phone: "+91 91997 77028",
  phoneHref: "+919199777028",
  address: "B-128, First Floor, Sector 2, Noida, Gautam Buddha Nagar – 201301, Uttar Pradesh, India",
  addressShort: "B-128, First Floor, Sector 2, Noida – 201301, UP, India",
  mapsEmbed:
    "https://maps.google.com/maps?q=B-128,%20Sector%202,%20Noida,%20Uttar%20Pradesh%20201301&t=&z=15&ie=UTF8&iwloc=&output=embed",
};

/** Central WhatsApp deep link — derived from the company number, no hardcoding. */
export const WHATSAPP_URL = `https://wa.me/${COMPANY.phoneHref.replace(/\D/g, "")}?text=${encodeURIComponent(
  "Hi Aviokan Technologies, I'd like to discuss a requirement."
)}`;

export const CAPABILITY_STRIP: string[] = [
  "Infrastructure", "Cybersecurity", "Cloud", "Networking", "Digital Engineering", "Managed Services", "Collaboration", "IoT",
];

export const STATS: StatItem[] = [
  { value: "10+", label: "Customers served" },
  { value: "10+", label: "Technology professionals" },
  { value: "12", label: "Technology pillars" },
  { value: "9", label: "Registrations & recognitions" },
];

export const APPROACH: ApproachStep[] = [
  { step: "Discover", desc: "We start with your business goals, users and constraints — not a product catalogue. Stakeholder workshops, site surveys and requirement mapping establish what success looks like." },
  { step: "Assess", desc: "Structured assessment of your current environment: infrastructure, network, security posture, applications, skills and risk. Evidence first, opinions second." },
  { step: "Design", desc: "Architecture that integrates domains — network, compute, security, cloud, workplace — into one coherent design with clear rationale, trade-offs and a costed roadmap." },
  { step: "Integrate", desc: "Procurement, staging and integration across vendors and platforms. We make components from different ecosystems behave as one managed system." },
  { step: "Deploy", desc: "Phased implementation with change control, validation gates and rollback points. Production cutovers are rehearsed, not improvised." },
  { step: "Secure", desc: "Security is verified at deployment and continuously after: hardening, monitoring, patch discipline and incident readiness built into operations." },
  { step: "Manage", desc: "Managed services, SLA-backed support and lifecycle management keep the environment healthy day to day — with reporting you can act on." },
  { step: "Transform", desc: "Quarterly reviews turn operational data into the next improvement: optimization, automation, modernization. The estate gets better every quarter." },
];

export const FUTURE_THEMES: FutureTheme[] = [
  { title: "AI & Intelligent Automation", desc: "Generative AI, chatbots, ML and RPA that remove manual effort.", icon: "sparkles" },
  { title: "Zero Trust Security", desc: "Identity-centric, continuously verified security across users, devices and data.", icon: "shield" },
  { title: "Hybrid & Multi-Cloud", desc: "Workloads placed where they run best, with unified governance.", icon: "cloud" },
  { title: "Edge & IoT", desc: "Connected sensors, edge compute and digital twins for smart operations.", icon: "cpu" },
  { title: "Software-Defined Everything", desc: "SD-WAN, virtualized and containerized platforms that scale on demand.", icon: "network" },
  { title: "Everything as a Service", desc: "Device-as-a-Service, managed wireless and consumption-based models.", icon: "refresh-cw" },
];

export const WHY_AVIOKAN: WhyItem[] = [
  { title: "Integrated Technology", desc: "Multiple technology domains under one system-integration approach — infrastructure, network, security, cloud and workplace designed together.", icon: "layers" },
  { title: "IIT & NIT-Led Engineering", desc: "Engineering leadership from the IITs and National Institutes of Technology (IITians & NITians) — design rigour and first-principles problem solving on every engagement.", icon: "compass" },
  { title: "Multi-Vendor Expertise", desc: "We architect solutions across technology ecosystems and vendor stacks, selecting what fits your environment rather than what fits a price list.", icon: "git-merge" },
  { title: "Security by Design", desc: "Security incorporated across infrastructure, cloud, applications and operations — not bolted on after go-live.", icon: "shield" },
  { title: "Lifecycle Support", desc: "From consulting and procurement through deployment, management and optimization — one accountable partner across the full lifecycle.", icon: "refresh-cw" },
  { title: "Business-Aligned Technology", desc: "Recommendations connect to actual business requirements and measurable outcomes, not technology for its own sake.", icon: "target" },
];

export const INDUSTRIES: Industry[] = [
  { name: "Banking & Financial Services", pillars: ["cybersecurity-resilience", "data-centre-infrastructure"] },
  { name: "Healthcare", pillars: ["data-centre-infrastructure", "cloud-digital-workplace"] },
  { name: "Manufacturing", pillars: ["network-connectivity", "collaboration-av-iot"] },
  { name: "Education", pillars: ["network-connectivity", "collaboration-av-iot"] },
  { name: "Government & PSU", pillars: ["data-centre-infrastructure", "managed-it-technology-services"] },
  { name: "IT & ITES", pillars: ["cloud-digital-workplace", "software-data-digital-engineering"] },
  { name: "Retail", pillars: ["network-connectivity", "cloud-digital-workplace"] },
  { name: "Logistics", pillars: ["network-connectivity", "managed-it-technology-services"] },
  { name: "Telecom", pillars: ["data-centre-infrastructure", "cybersecurity-resilience"] },
  { name: "Professional Services", pillars: ["cloud-digital-workplace", "managed-it-technology-services"] },
];

export const CERTIFICATIONS: Certification[] = [
  { name: "MSME", type: "Registration", status: "registered", desc: "Ministry of MSME registration.", logo: "/brand/certs/msme.svg" },
  { name: "Udyam", type: "Registration", status: "registered", desc: "Udyam (MSME) registration.", logo: "/brand/certs/udyam.svg" },
  { name: "Startup India", type: "Recognition", status: "registered", desc: "DPIIT Startup India recognition.", logo: "/brand/certs/startup-india.svg" },
  { name: "DPIIT", type: "Recognition", status: "registered", desc: "Department for Promotion of Industry and Internal Trade.", logo: "/brand/certs/dpiit.svg" },
  { name: "Trademark", type: "IP", status: "registered", desc: "AVIOKAN brand protected under the TradeMarks Act, 1999.", logo: "/brand/certs/trademark.svg" },
  { name: "IEC", type: "Registration", status: "registered", desc: "Importer Exporter Code (DGFT-issued).", logo: "/brand/certs/iec.svg" },
  { name: "ICEGATE", type: "Registration", status: "registered", desc: "Registered on Indian Customs EDI Gateway for electronic customs filing.", logo: "/brand/certs/icegate.svg" },
  { name: "ISO 9001", type: "Certification / Compliance Roadmap", status: "roadmap", desc: "Quality management system standard.", logo: "/brand/certs/iso-9001.svg" },
  { name: "ISO 27001", type: "Certification / Compliance Roadmap", status: "roadmap", desc: "Information security management system standard.", logo: "/brand/certs/iso-27001.svg" },
];

export const NAV: NavItem[] = [
  { label: "HOME", to: "/" },
  {
    label: "ABOUT",
    children: [
      { label: "About Aviokan", to: "/about" },
      { label: "Mission & Vision", to: "/about/mission-vision" },
      { label: "Our Approach", to: "/about/approach" },
      { label: "Blog", to: "/about/blog" },
      { label: "Careers", to: "/about/careers" },
    ],
  },
  { label: "SOLUTIONS", mega: true },
  { label: "DIGITAL ENGINEERING", to: "/digital-engineering" },
  {
    label: "PARTNERS & CLIENTS",
    children: [
      { label: "Technology Partners", to: "/partners" },
      { label: "Clients", to: "/clients" },
    ],
  },
  { label: "CONTACT", to: "/contact" },
];

export const LEGAL_PAGES: Record<string, LegalPage> = {
  "privacy-policy": {
    title: "Privacy Policy",
    body: [
      ["Information we collect", "Aviokan Technologies Private Limited collects information you provide through this website — such as your name, company, email address, phone number and enquiry or application details — strictly to respond to your request or process your application."],
      ["How we use it", "Contact details are used to respond to enquiries, schedule consultations and evaluate applications. We do not sell or rent personal information to third parties."],
      ["Data retention & security", "Enquiry and application data is stored securely with access limited to authorized Aviokan personnel. You may request access, correction or deletion of your personal information by writing to connect@aviokan.com."],
      ["Cookies", "This website uses only functional cookies necessary for site operation and, where enabled, privacy-respecting analytics. You can control cookies through your browser settings."],
      ["Contact", "For any privacy question, write to connect@aviokan.com or to our registered address: B-128, First Floor, Sector 2, Noida, Gautam Buddha Nagar – 201301, Uttar Pradesh, India."],
    ],
  },
  "terms-conditions": {
    title: "Terms & Conditions",
    body: [
      ["Use of this website", "This website is operated by Aviokan Technologies Private Limited. By using it you agree to use the content and services for lawful purposes only."],
      ["Content", "All text, graphics, logos and page content are the property of Aviokan Technologies Private Limited or used with permission. Company and technology names mentioned are trademarks of their respective owners."],
      ["No warranty", "Website content is provided for general information. While we work to keep it accurate and current, Aviokan makes no warranties of any kind about completeness, reliability or suitability."],
      ["Liability", "Aviokan shall not be liable for any loss or damage arising from the use of this website, to the extent permitted by law."],
      ["Governing law", "These terms are governed by the laws of India, with jurisdiction in Gautam Buddha Nagar, Uttar Pradesh."],
    ],
  },
  "cookie-policy": {
    title: "Cookie Policy",
    body: [
      ["What we use", "This website uses only essential cookies required for site functionality and, where enabled, aggregate analytics to understand overall site usage."],
      ["No tracking profiles", "We do not build advertising profiles of individual visitors and do not sell browsing behaviour to third parties."],
      ["Managing cookies", "You may disable cookies in your browser settings. The website remains usable with essential cookies blocked, though some interactive features may be limited."],
      ["Questions", "Write to connect@aviokan.com for any question about this policy."],
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    body: [
      ["General information", "Content on this website describes the capabilities and service areas of Aviokan Technologies Private Limited for general information purposes."],
      ["Technology names & partner ecosystem", "All OEM and technology names, logos and trademarks displayed are the property of their respective owners. Their appearance indicates a technology ecosystem relationship only and does not imply authorized, certified or endorsed partnership status unless explicitly stated."],
      ["Certifications & registrations", "Certification and registration items are shown with their current status. Items marked 'Certification / Compliance Roadmap' indicate planned rather than completed certifications."],
      ["Case studies", "Case study content marked as placeholder is illustrative. Real customer references are published only with customer authorization."],
      ["No professional advice", "Website content does not constitute professional, legal or security advice for your specific environment. Engage Aviokan for an assessment of your requirements."],
    ],
  },
};

export const SOCIALS: Social[] = [
  { name: "LinkedIn", icon: "linkedin", href: "https://www.linkedin.com/company/143997855/" },
  { name: "Instagram", icon: "instagram", href: "https://www.instagram.com/aviokan_tech/" },
  { name: "Facebook", icon: "facebook", href: "https://www.facebook.com/profile.php?id=61595587882851" },
  { name: "YouTube", icon: "youtube", href: "https://www.youtube.com/channel/UChWpL_yTkn0KCkO6E0CRpEw" },
];

/** key → url map used as the fallback when the settings document has placeholders. */
export const SOCIAL_HREFS: Record<string, string> = Object.fromEntries(SOCIALS.map((s) => [s.name.toLowerCase(), s.href]));

export const ORG_JSONLD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Aviokan Technologies Private Limited",
  alternateName: "Aviokan Technologies",
  slogan: "Integrate. Innovate. Transform.",
  description: "Enterprise technology solutions: IT system integration, data centre, networking, cybersecurity, cloud, collaboration, digital engineering and managed IT services.",
  url: "https://www.aviokan.com",
  email: "connect@aviokan.com",
  telephone: "+91 91997 77028",
  address: {
    "@type": "PostalAddress",
    streetAddress: "B-128, First Floor, Sector 2",
    addressLocality: "Noida",
    addressRegion: "Uttar Pradesh",
    postalCode: "201301",
    addressCountry: "IN",
  },
  knowsAbout: ["IT System Integration", "Cybersecurity", "Cloud Computing", "Data Centre Infrastructure", "Managed IT Services", "Digital Engineering"],
  sameAs: SOCIALS.map((s) => s.href),
};
